import { useEffect, useState } from 'react';
import { upperMantendoCursor } from '../../../components/inputs/UpperInput';
import { Loader2, Plus, Trash2, Tag, Check, Search, X, Eye, EyeOff } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { useDialog } from '../../../components/DialogProvider';
import { atendimentoApi } from '../lib/api';
import { useEtiquetas } from '../lib/etiquetas-context';
import { BadgeEtiqueta } from '../componentes/BadgeEtiqueta';
import type { Etiqueta } from '../lib/tipos';

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

const PALETA = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#64748b', '#607D8B', '#000000',
];

// Grade compartilhada pelos TRES: cabecalho do bloco de criacao, cabecalho do grid e as
// linhas. Nome e Apelido em `minmax(0,1fr)` pra ficarem do mesmo tamanho — `1fr` puro nao
// encolhe abaixo do conteudo e o nome mais longo comeria a largura do vizinho.
// A ultima coluna 112 -> 140 com o olho ([DA-813]); o modal cresce o mesmo tanto.
// A coluna "Na Gestao" (72px) entrou depois de "Nao cliente" em 15/10/2026 ([DA-814] p.7); o modal
// cresce o mesmo tanto.
const COLUNAS = '132px minmax(0,1fr) minmax(0,1fr) 64px 84px 72px 88px 56px 140px';

/** [DA-814] p.7: a caixa so' vale pra etiqueta de NAO CLIENTE — as comuns ja aparecem sempre. */
const AJUDA_NA_GESTAO =
  'Marcada: esta etiqueta de "Nao cliente" aparece na lista de etiquetas das abas Contatos e '
  + 'Invalidos da Gestao de Vendas, pra voce marcar contatos com ela por la. So muda a EXIBICAO: '
  + 'quem carrega a etiqueta continua fora da Gestao de Vendas.';

/** Sem acento e sem caixa — a busca casa `aquario` digitando `AQUÁRIO`.
 *  ⚠️ O intervalo do replace sao os COMBINING (U+0300..U+036F) escritos literalmente,
 *  entao no editor eles aparecem como caractere invisivel. Conferido em bytes:
 *  CC 80 .. CD AF. Ao mexer nesta linha, conferir o `od -c` — reencoding do arquivo
 *  quebra o intervalo sem erro nenhum, e o sintoma seria so a busca por termo com
 *  acento parar de casar. */
function achatar(s: string): string {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

interface DraftEdicao {
  id: number;
  nome: string;
  cor: string;
  apelido: string | null;
}

/** Rotulo curto do que a caixa faz — a coluna sozinha nao diz ONDE ela some. */
const AJUDA_OCULTAR =
  'Marcada: a etiqueta some das telas de operacao (ficha do contato, card da lista e filtros) '
  + 'e continua so aqui. O vinculo com os contatos NAO e apagado.';

/** ⚠️ Esta caixa NAO e' exibicao — ela muda o que a Gestao de Vendas conta. O rotulo da
 *  coluna sozinho ("Nao cliente") nao diz isso, e marcar por engano some com pedido. */
const AJUDA_NAO_CLIENTE =
  'Marcada: quem carrega esta etiqueta some da Gestao de Vendas inteira — abas, contadores, '
  + 'agenda e estatisticas. O contato continua normal no chat do Atendimento. '
  + 'Serve pra quem escreve mas nunca vai comprar: fornecedor, transportadora, candidato a vaga.';

/** [DA-811]: a regua da campanha de fly passou a ser POR ETIQUETA. */
const AJUDA_FREQUENCIA =
  'De quantos em quantos dias quem tem esta etiqueta pode receber fly da loja (1 = todo dia). '
  + 'Quem tem varias etiquetas segue a de MENOR valor. Vale para a campanha e conta tambem o fly '
  + 'que a vendedora manda no chat.';

const CHAVE_MOSTRAR_ESCONDIDAS = 'etiquetas.mostrarEscondidas';

/** Inteiro 1..365 — o mesmo CHECK do banco. Qualquer outra coisa devolve `null`. */
function lerFrequencia(texto: string): number | null {
  if (!/^\d{1,3}$/.test(texto.trim())) return null;
  const n = Number(texto.trim());
  return n >= 1 && n <= 365 ? n : null;
}

/**
 * O numero de dias na propria linha, gravando ao sair do campo (ou no Enter) — mesmo gesto das
 * caixas Ocultar/Nao cliente, sem obrigar a abrir Editar.
 *
 * ⛔ Nunca grava vazio nem invalido (pedido dele: nenhum registro em branco): sair do campo com
 * algo fora de 1..365 VOLTA o valor gravado, e a linha de baixo diz por que.
 */
function CampoFrequencia({ valor, onGravar }: { valor: number; onGravar: (n: number) => Promise<boolean> }) {
  const [texto, setTexto] = useState(String(valor));
  const [gravando, setGravando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  useEffect(() => { setTexto(String(valor)); }, [valor]);

  async function confirmar() {
    const n = lerFrequencia(texto);
    if (n === null) {
      setAviso('Use de 1 a 365 dias');
      setTexto(String(valor));
      return;
    }
    setAviso(null);
    if (n === valor) return;
    setGravando(true);
    const ok = await onGravar(n);
    setGravando(false);
    if (!ok) setTexto(String(valor));
  }

  return (
    <span className="flex flex-col items-center">
      <span className="flex items-center gap-1">
        {gravando ? (
          <Loader2 size={14} className="animate-spin opacity-60" />
        ) : (
          <input
            type="text"
            inputMode="numeric"
            value={texto}
            onChange={(e) => setTexto(e.target.value.replace(/\D/g, '').slice(0, 3))}
            onBlur={confirmar}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
            title={AJUDA_FREQUENCIA}
            className="w-11 px-1 py-0.5 rounded border text-sm text-center outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', padding: '2px 4px' }}
          />
        )}
        <span className="text-[11px] opacity-70">{texto === '1' ? 'dia' : 'dias'}</span>
      </span>
      {aviso && <span className="text-[10px] text-amber-700 whitespace-nowrap">{aviso}</span>}
    </span>
  );
}

export function ModalEtiquetas({ aberto, onFechar }: Props) {
  const toast = useToast();
  const dialog = useDialog();
  const { atualizar } = useEtiquetas();

  const [lista, setLista] = useState<Etiqueta[]>([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvandoId, setSalvandoId] = useState<number | null>(null);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);
  const [draft, setDraft] = useState<DraftEdicao | null>(null);
  const [novoNome, setNovoNome] = useState('');
  const [novoCor, setNovoCor] = useState(PALETA[18]!);
  const [novoApelido, setNovoApelido] = useState('');
  const [novoOcultar, setNovoOcultar] = useState(false);
  const [novoNaoCliente, setNovoNaoCliente] = useState(false);
  // Nasce 7, o padrao do banco ([DA-811]) — nunca vazio.
  const [novoFrequencia, setNovoFrequencia] = useState('7');
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [alternandoId, setAlternandoId] = useState<number | null>(null);
  const [alternandoNCId, setAlternandoNCId] = useState<number | null>(null);
  /**
   * A ultima linha que ele mexeu, pintada de amarelo — pedido dele em 11/09/2026, junto com o
   * conserto do scroll. E' o RECIBO do gesto: numa lista de 28, marcar uma caixa no meio nao
   * devolvia sinal nenhum de QUAL linha acabou de mudar.
   *
   * ⚠️ O `#fde68a` so' entra aqui porque foi CONFERIDO que ele nao significa outra coisa nesta
   * tela: medidas as 28 etiquetas, as cores em uso sao #607D8B, #6366f1, #84cc16, #86efac e
   * #c4b5fd — nenhuma amarela, entao a pill nao some dentro do destaque.
   */
  const [linhaMexida, setLinhaMexida] = useState<number | null>(null);

  /**
   * Etiquetas de USUARIO ([DA-813], pedidos dele 15/10/2026): as que o sistema cria com o nome de
   * quem atende — de quem esta ativo e de ex-funcionario. Saem da lista por padrao e voltam com a
   * caixa "Mostrar etiquetas de usuarios". Quem decide e' o `de_agente` do servidor (nome OU login,
   * [DA-812]) — uma regra so', nada copiado aqui. Nada e' apagado: o carimbo segue nos contatos.
   */
  //
  // ➕ O "olhinho" (pedido dele, mesmo dia, migration 0318): esconde QUALQUER etiqueta desta lista
  // — so' desta tela. Escondida = de usuario OU marcada no olho; a caixa mostra as duas juntas.
  const [mostrarEscondidas, setMostrarEscondidas] = useState<boolean>(() => {
    try { return localStorage.getItem(CHAVE_MOSTRAR_ESCONDIDAS) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(CHAVE_MOSTRAR_ESCONDIDAS, mostrarEscondidas ? '1' : '0'); } catch { /* sem storage */ }
  }, [mostrarEscondidas]);
  const deUsuario = (et: Etiqueta) => !!et.de_agente;
  const escondida = (et: Etiqueta) => deUsuario(et) || !!et.escondida_gerenciar;
  const qtdEscondidas = lista.filter(escondida).length;
  const [alternandoOlhoId, setAlternandoOlhoId] = useState<number | null>(null);

  /** O olho da linha: grava na hora, otimista com desfazer no erro — o gesto das caixas ao lado. */
  async function alternarEscondida(et: Etiqueta) {
    const alvo = !et.escondida_gerenciar;
    setAlternandoOlhoId(et.id);
    setLinhaMexida(et.id);
    setLista((l) => l.map((x) => (x.id === et.id ? { ...x, escondida_gerenciar: alvo } : x)));
    try {
      await atendimentoApi.atualizarEtiqueta(et.id, { escondida_gerenciar: alvo });
      await recarregar(true);
      if (alvo && !mostrarEscondidas) toast.sucesso(`"${et.nome}" escondida desta lista — "Mostrar escondidas" traz de volta`);
    } catch (e: any) {
      setLista((l) => l.map((x) => (x.id === et.id ? { ...x, escondida_gerenciar: !alvo } : x)));
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setAlternandoOlhoId(null);
    }
  }

  // "Na Gestao" ([DA-814] p.7, pedido dele: *"eu consigo habilitar a exibicao?"*). So' EXIBICAO no
  // seletor da Gestao de Vendas — grava na hora, sem confirmacao, como o Ocultar ao lado.
  const [alternandoGestaoId, setAlternandoGestaoId] = useState<number | null>(null);
  async function alternarExibirGestao(et: Etiqueta) {
    const alvo = !et.exibir_gestao;
    setAlternandoGestaoId(et.id);
    setLinhaMexida(et.id);
    setLista((l) => l.map((x) => (x.id === et.id ? { ...x, exibir_gestao: alvo } : x)));
    try {
      await atendimentoApi.atualizarEtiqueta(et.id, { exibir_gestao: alvo });
      await recarregar(true);
    } catch (e: any) {
      setLista((l) => l.map((x) => (x.id === et.id ? { ...x, exibir_gestao: !alvo } : x)));
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setAlternandoGestaoId(null);
    }
  }

  const termo = achatar(busca.trim());
  const visiveis = mostrarEscondidas ? lista : lista.filter((e) => !escondida(e));
  const filtradas = termo
    ? visiveis.filter((e) => achatar(e.nome).includes(termo) || achatar(e.apelido ?? '').includes(termo))
    : visiveis;

  /**
   * ⛔ `silencioso` NAO e' detalhe de conforto — e' o que impede a lista de VOLTAR AO TOPO.
   *
   * A lista inteira mora dentro de `{!carregando && ...}`, entao acender o spinner DESMONTA o
   * container rolavel; o que volta e' um container novo, e todo container novo nasce com
   * `scrollTop = 0`. Marcando a 20a etiqueta, ele era jogado de volta pra primeira a cada clique
   * (relato dele, 11/09/2026). E' a [DA-298] com outra roupa: recarga que veio de uma acao do
   * proprio operador troca o dado POR BAIXO, sem desmontar nada.
   *
   * O spinner fica so' na carga de ABERTURA, que e' quando nao ha lista nenhuma pra preservar.
   */
  async function recarregar(silencioso = false) {
    if (!silencioso) setCarregando(true);
    try {
      const lst = await atendimentoApi.listarEtiquetas();
      setLista(lst);
      atualizar(lst);
    } catch {
      toast.erro('Falha ao carregar etiquetas');
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }

  useEffect(() => {
    if (!aberto) return;
    recarregar();
    setDraft(null);
    setBusca('');
    setNovoNome('');
    setNovoApelido('');
    setNovoOcultar(false);
    setNovoNaoCliente(false);
    setNovoFrequencia('7');
    setNovoCor(PALETA[18]!);
  }, [aberto]);

  /** Grava a frequencia de fly da linha. Devolve se deu certo — o campo volta o valor no erro. */
  async function gravarFrequencia(et: Etiqueta, dias: number): Promise<boolean> {
    setLinhaMexida(et.id);
    try {
      await atendimentoApi.atualizarEtiqueta(et.id, { frequencia_fly_dias: dias });
      setLista((l) => l.map((x) => (x.id === et.id ? { ...x, frequencia_fly_dias: dias } : x)));
      await recarregar(true);
      return true;
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao salvar');
      return false;
    }
  }

  /** Alterna direto na linha — e' exibicao, reversivel no mesmo clique, e nao vale
   *  obrigar a entrar em Editar so pra isso. Otimista com desfazer no erro. */
  async function alternarOcultar(et: Etiqueta) {
    const alvo = !et.ocultar;
    setAlternandoId(et.id);
    setLinhaMexida(et.id);
    setLista((l) => l.map((x) => (x.id === et.id ? { ...x, ocultar: alvo } : x)));
    try {
      await atendimentoApi.atualizarEtiqueta(et.id, { ocultar: alvo });
      await recarregar(true);
    } catch (e: any) {
      setLista((l) => l.map((x) => (x.id === et.id ? { ...x, ocultar: !alvo } : x)));
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setAlternandoId(null);
    }
  }

  /**
   * ⚠️ Esta caixa NAO e' irma da de cima. `ocultar` e' exibicao; esta MUDA o que a Gestao
   * de Vendas conta — e marcar a `cristina` por engano esconderia 304 contatos de uma vez.
   * Por isso MARCAR pede confirmacao com a contagem dentro ([DA-685] p.4: ato que esconde
   * precisa dizer o que esta escondendo); DESMARCAR devolve tudo e vai direto.
   */
  async function alternarNaoCliente(et: Etiqueta) {
    const alvo = !et.nao_cliente;
    if (alvo) {
      const n = et.contatos ?? 0;
      const ok = await dialog.confirmar(
        `Marcar "${et.nome}" como quem NAO e cliente?\n\n`
        + `${n === 1 ? 'O contato que carrega' : `Os ${n} contatos que carregam`} esta etiqueta `
        + 'somem da Gestao de Vendas — abas, contadores, agenda e estatisticas —, junto com os '
        + 'pedidos deles. No chat do Atendimento nada muda.',
        { titulo: 'Esconder da Gestao de Vendas?', textoConfirmar: 'Marcar' },
      );
      if (!ok) return;
    }
    setAlternandoNCId(et.id);
    setLinhaMexida(et.id);
    setLista((l) => l.map((x) => (x.id === et.id ? { ...x, nao_cliente: alvo } : x)));
    try {
      await atendimentoApi.atualizarEtiqueta(et.id, { nao_cliente: alvo });
      await recarregar(true);
    } catch (e: any) {
      setLista((l) => l.map((x) => (x.id === et.id ? { ...x, nao_cliente: !alvo } : x)));
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setAlternandoNCId(null);
    }
  }

  async function salvarEdicao() {
    if (!draft) return;
    if (!draft.nome.trim()) { toast.erro('Nome obrigatorio'); return; }
    if (!draft.apelido?.trim()) { toast.erro('Apelido obrigatorio — e o texto da pillzinha'); return; }
    setSalvandoId(draft.id);
    try {
      await atendimentoApi.atualizarEtiqueta(draft.id, {
        nome: draft.nome.trim(),
        cor: draft.cor,
        apelido: draft.apelido?.trim() || null,
      });
      toast.sucesso('Etiqueta atualizada');
      setDraft(null);
      await recarregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao salvar');
    } finally {
      setSalvandoId(null);
    }
  }

  async function excluir(et: Etiqueta) {
    // So chega aqui com `contatos === 0` — a lixeira nasce desabilitada em uso, e o
    // DELETE recusa de novo no servidor.
    const ok = await dialog.confirmar(
      `Excluir a etiqueta "${et.nome}"? Nenhum contato usa ela hoje.`,
      { titulo: 'Excluir etiqueta?', textoConfirmar: 'Excluir', perigoso: true },
    );
    if (!ok) return;
    setExcluindoId(et.id);
    try {
      await atendimentoApi.excluirEtiqueta(et.id);
      toast.sucesso('Etiqueta excluida');
      await recarregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao excluir');
    } finally {
      setExcluindoId(null);
    }
  }

  const novoFrequenciaValida = lerFrequencia(novoFrequencia);

  async function criarNova() {
    if (!novoNome.trim() || !novoApelido.trim() || novoFrequenciaValida === null) return;
    setCriandoNovo(true);
    try {
      await atendimentoApi.criarEtiqueta({
        nome: novoNome.trim(),
        cor: novoCor,
        apelido: novoApelido.trim() || null,
        ocultar: novoOcultar,
        nao_cliente: novoNaoCliente,
        frequencia_fly_dias: novoFrequenciaValida,
      });
      toast.sucesso('Etiqueta criada');
      setNovoNome('');
      setNovoApelido('');
      setNovoOcultar(false);
      setNovoNaoCliente(false);
      setNovoFrequencia('7');
      setNovoCor(PALETA[18]!);
      await recarregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao criar');
    } finally {
      setCriandoNovo(false);
    }
  }

  // ⚠️ 740 -> 830 com a coluna "Nao cliente" ([DA-693]): sem isto Nome e Apelido, que sao
  // os dois `minmax(0,1fr)` da grade, perdiam 84px e o nome comprido quebrava em duas linhas.
  // 830 -> 926 com a "Fly a cada" ([DA-811]), pelo mesmo motivo: 88px da coluna + o gap.
  // 926 -> 954 com o olho ([DA-813]): +28 na ultima coluna.
  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo="Etiquetas" largura={1034}
      rodape={<button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>}>
      <div className="mb-3 text-xs opacity-70 flex items-start gap-2">
        <Tag size={14} className="mt-0.5 flex-shrink-0" />
        <span>Etiquetas sao cross-inbox (mesmo JID compartilha label entre Atendimento e Compras). O <b>apelido</b> e o texto curto que aparece na pillzinha colorida nas listas. So da' pra excluir etiqueta com <b>0</b> em uso. <b>Ocultar</b> tira a etiqueta das telas de operacao (ficha do contato, card e filtros) e a deixa so' aqui — o vinculo com os contatos continua gravado; as do sistema, com o nome de quem atende, nascem marcadas. <b>Nao cliente</b> e' diferente: quem carrega a etiqueta some da <b>Gestao de Vendas</b> inteira (abas, contadores, agenda e estatisticas) e continua normal no chat. <b>Fly a cada</b> e' de quantos em quantos dias quem tem a etiqueta pode receber fly (1 = todo dia); com varias etiquetas, vale a de menor valor. O <b>olho</b> esconde a etiqueta so' desta lista — ela continua valendo nos contatos e nas outras telas.</span>
      </div>

      {/* Form de criacao */}
      <div className="p-3 mb-3 rounded-lg" style={{ backgroundColor: 'rgba(0,0,0,0.04)', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}>
        {/* Cabecalho PROPRIO do bloco, com os mesmos nomes das colunas do grid (pedido do
            Ruan, 03/09/2026) — e por isso os campos nao levam placeholder: o rotulo acima
            ja nomeia, e "ex: VIP" era um valor copiavel dentro do campo ([DA-608]). */}
        <div className="grid gap-2 pb-1 text-[10px] uppercase tracking-wide opacity-55" style={{ gridTemplateColumns: COLUNAS }}>
          <span>Como aparece</span>
          <span>Nome</span>
          <span>Apelido</span>
          <span className="text-center">Ocultar</span>
          <span className="text-center">Nao cliente</span>
          <span className="text-center">Na Gestao</span>
          <span className="text-center">Fly a cada</span>
          <span />
          <span />
        </div>
        <div className="grid gap-2 items-center" style={{ gridTemplateColumns: COLUNAS }}>
          <span className="truncate">
            <BadgeEtiqueta nome={novoNome || 'PREVIEW'} cor={novoCor} apelido={novoApelido || null} />
          </span>
          <input
            type="text"
            value={novoNome}
            onChange={(e) => setNovoNome(e.target.value)}
            maxLength={60}
            disabled={criandoNovo}
            className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
          />
          <input
            type="text"
            value={novoApelido}
            // O uppercase dos inputs e' SO CSS — sem isto a tela mostra maiuscula e
            // GRAVA o que foi digitado (foi assim que nasceu o `FORNECEDOR merc`).
            onChange={(e) => setNovoApelido(upperMantendoCursor(e))}
            maxLength={30}
            disabled={criandoNovo}
            className="w-full px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
          />
          {/* Nasce DESMARCADA: quem cria aqui e' ele, a mao, e etiqueta criada a mao e'
              categoria. Marcada de saida so' a que o rodizio cria. */}
          <span className="flex justify-center">
            <input
              type="checkbox"
              checked={novoOcultar}
              onChange={(e) => setNovoOcultar(e.target.checked)}
              disabled={criandoNovo}
              title={AJUDA_OCULTAR}
              className="w-4 h-4 rounded text-blue-600 cursor-pointer"
            />
          </span>
          {/* Tambem nasce DESMARCADA — caixa pre-marcada numa tela que grava e' a decisao
              tomada por quem desenhou, nao por quem clica. */}
          <span className="flex justify-center">
            <input
              type="checkbox"
              checked={novoNaoCliente}
              onChange={(e) => setNovoNaoCliente(e.target.checked)}
              disabled={criandoNovo}
              title={AJUDA_NAO_CLIENTE}
              className="w-4 h-4 rounded text-blue-600 cursor-pointer"
            />
          </span>
          {/* "Na Gestao" nao tem campo na criacao: so' vale pra etiqueta ja marcada como Nao
              cliente, e nasce desligada — liga-se na linha, depois de criada. */}
          <span />
          <span className="flex items-center justify-center gap-1">
            <input
              type="text"
              inputMode="numeric"
              value={novoFrequencia}
              onChange={(e) => setNovoFrequencia(e.target.value.replace(/\D/g, '').slice(0, 3))}
              disabled={criandoNovo}
              title={AJUDA_FREQUENCIA}
              className="w-11 rounded border text-sm text-center outline-none focus:ring-2 focus:ring-blue-400"
              style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', padding: '2px 4px' }}
            />
            <span className="text-[11px] opacity-70">{novoFrequencia === '1' ? 'dia' : 'dias'}</span>
          </span>
          <span />
          <button
            type="button"
            onClick={criarNova}
            disabled={!novoNome.trim() || !novoApelido.trim() || novoFrequenciaValida === null || criandoNovo}
            className="btn-primario flex items-center justify-center gap-1.5 disabled:opacity-50 w-full"
          >
            {criandoNovo ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Criar
          </button>
        </div>
        {/* Botao desabilitado DIZ por que. O apelido e' o texto da pillzinha: sem ele a
            etiqueta apareceria com o nome inteiro nas listas. */}
        {(!novoNome.trim() || !novoApelido.trim()) && (
          <div className="text-[11px] mt-1.5 text-amber-700">
            {!novoNome.trim() && !novoApelido.trim()
              ? 'Preencha o nome e o apelido — o apelido e o texto que aparece na pillzinha.'
              : !novoNome.trim()
                ? 'Falta o nome.'
                : 'Falta o apelido — e o texto que aparece na pillzinha das listas.'}
          </div>
        )}
        {novoNome.trim() && novoApelido.trim() && novoFrequenciaValida === null && (
          <div className="text-[11px] mt-1.5 text-amber-700">
            A frequência de fly vai de 1 a 365 dias.
          </div>
        )}
        <div className="flex flex-wrap gap-1 mt-2">
          {PALETA.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setNovoCor(c)}
              className="w-5 h-5 rounded-full border-2 transition"
              style={{ backgroundColor: c, borderColor: novoCor === c ? '#000' : 'transparent' }}
              title={c}
            />
          ))}
        </div>
      </div>

      {/* Lista */}
      {carregando && (
        <div className="flex items-center justify-center py-8 opacity-70">
          <Loader2 size={18} className="animate-spin" />
        </div>
      )}

      {/* Busca — client-side sobre a lista INTEIRA (o GET traz todas, nao ha LIMIT pra
          filtrar por cima). Casa nome e apelido, que sao as duas colunas visiveis. */}
      {!carregando && lista.length > 0 && (
        <div className="relative mb-2">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou apelido..."
            className="w-full pl-8 pr-8 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
            style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
          />
          {busca && (
            <button
              type="button"
              onClick={() => setBusca('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
              title="Limpar busca"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      {/* A caixa so' existe quando ha o que esconder — e diz quantas, pra ausencia nao se
          ler como inexistencia ([DA-468]). */}
      {!carregando && qtdEscondidas > 0 && (
        <label className="flex items-center gap-2 mb-2 text-xs cursor-pointer select-none" style={{ color: 'var(--text-muted)' }}>
          <input
            type="checkbox"
            checked={mostrarEscondidas}
            onChange={(e) => setMostrarEscondidas(e.target.checked)}
            className="w-4 h-4 rounded text-blue-600 cursor-pointer"
          />
          Mostrar escondidas ({qtdEscondidas}) — as escondidas pelo olho e as de usuário (com o nome de quem atende, inclusive ex-funcionários)
        </label>
      )}

      {!carregando && lista.length === 0 && (
        <div className="text-center py-8 opacity-60 text-sm">Nenhuma etiqueta cadastrada.</div>
      )}

      {/* Ausencia nao se distingue de inexistencia: o vazio da BUSCA nomeia o termo e
          oferece a saida, em vez de dizer que nao ha etiqueta ([DA-468]). */}
      {!carregando && lista.length > 0 && filtradas.length === 0 && (
        <div className="text-center py-8 opacity-70 text-sm">
          Nenhuma etiqueta com <b>{busca}</b> — das {lista.length} cadastradas.
          {!mostrarEscondidas && termo && lista.some((e) => escondida(e) && (achatar(e.nome).includes(termo) || achatar(e.apelido ?? '').includes(termo))) && (
            <> Há etiqueta escondida com esse texto — marque “Mostrar escondidas”.</>
          )}
          <button type="button" onClick={() => setBusca('')} className="ml-2 text-blue-600 hover:text-blue-800 underline">
            limpar busca
          </button>
        </div>
      )}

      {!carregando && filtradas.length > 0 && (
        <div
          className="grid gap-2 px-2.5 pb-1 pr-2 text-[10px] uppercase tracking-wide opacity-55"
          style={{ gridTemplateColumns: COLUNAS }}
        >
          <span>Como aparece</span>
          <span>Nome</span>
          <span>Apelido</span>
          <span className="text-center" title={AJUDA_OCULTAR}>Ocultar</span>
          <span className="text-center" title={AJUDA_NAO_CLIENTE}>Nao cliente</span>
          <span className="text-center" title={AJUDA_NA_GESTAO}>Na Gestao</span>
          <span className="text-center" title={AJUDA_FREQUENCIA}>Fly a cada</span>
          <span className="text-right">Em uso</span>
          <span />
        </div>
      )}

      {!carregando && filtradas.length > 0 && (
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
          {filtradas.map((et) => {
            const editando = draft?.id === et.id;
            const emUso = et.contatos ?? 0;
            return (
              // ⚠️ Na linha marcada a LETRA volta junto: o texto herda a cor do tema, que no
              // escuro e' clara e sumiria no amarelo. Mesma receita do grid da casa.
              <div
                key={et.id}
                className="p-2.5 rounded-lg"
                style={linhaMexida === et.id
                  ? { backgroundColor: '#fde68a', color: '#000', border: '1px solid #b45309' }
                  : { backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid var(--border, rgba(0,0,0,0.08))' }}
              >
                {!editando ? (
                  <div className="grid gap-2 items-center" style={{ gridTemplateColumns: COLUNAS }}>
                    <span className="truncate"><BadgeEtiqueta nome={et.nome} cor={et.cor} apelido={et.apelido} /></span>
                    <span className="text-sm truncate">{et.nome}</span>
                    <span className="text-xs opacity-70 truncate">{et.apelido ?? '—'}</span>
                    {/* Acao POR LINHA, gravando na hora: e' exibicao e se desfaz no mesmo
                        clique, entao nao vale mandar ele abrir Editar so' pra isso. */}
                    <span className="flex justify-center">
                      {alternandoId === et.id ? (
                        <Loader2 size={14} className="animate-spin opacity-60" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={!!et.ocultar}
                          onChange={() => alternarOcultar(et)}
                          title={AJUDA_OCULTAR}
                          className="w-4 h-4 rounded text-blue-600 cursor-pointer"
                        />
                      )}
                    </span>
                    <span className="flex justify-center">
                      {alternandoNCId === et.id ? (
                        <Loader2 size={14} className="animate-spin opacity-60" />
                      ) : (
                        // ⛔ Na etiqueta de AGENTE a caixa fica CINZA, nao some ([DA-468]:
                        // ausencia nao se distingue de inexistencia). O `title` diz por que e
                        // aponta a caixa certa — a de Ocultar, que e' o efeito visual.
                        <input
                          type="checkbox"
                          checked={!!et.nao_cliente}
                          disabled={!!et.de_agente}
                          onChange={() => alternarNaoCliente(et)}
                          title={et.de_agente
                            ? `"${et.nome}" e a etiqueta de quem ATENDE. Marcar aqui esconderia da `
                              + 'Gestao de Vendas os clientes que essa pessoa atendeu. Para sumir '
                              + 'com a etiqueta das telas, use a caixa Ocultar ao lado.'
                            : AJUDA_NAO_CLIENTE}
                          className={et.de_agente
                            ? 'w-4 h-4 rounded opacity-40 cursor-not-allowed'
                            : 'w-4 h-4 rounded text-blue-600 cursor-pointer'}
                        />
                      )}
                    </span>
                    {/* "Na Gestao" ([DA-814] p.7). Na etiqueta COMUM a caixa fica cinza, nao some
                        ([DA-468]): ela ja aparece sempre, e o `title` diz isso. */}
                    <span className="flex justify-center">
                      {alternandoGestaoId === et.id ? (
                        <Loader2 size={14} className="animate-spin opacity-60" />
                      ) : (
                        <input
                          type="checkbox"
                          checked={!!et.nao_cliente && !!et.exibir_gestao}
                          disabled={!et.nao_cliente}
                          onChange={() => alternarExibirGestao(et)}
                          title={et.nao_cliente
                            ? AJUDA_NA_GESTAO
                            : 'Etiqueta comum ja aparece sempre na Gestao de Vendas — esta caixa so vale para etiqueta de "Nao cliente".'}
                          className={et.nao_cliente
                            ? 'w-4 h-4 rounded text-blue-600 cursor-pointer'
                            : 'w-4 h-4 rounded opacity-40 cursor-not-allowed'}
                        />
                      )}
                    </span>
                    <CampoFrequencia
                      valor={et.frequencia_fly_dias ?? 7}
                      onGravar={(n) => gravarFrequencia(et, n)}
                    />
                    {/* A lixeira so abre com 0 aqui do lado — e o cabecalho do modal diz isso. */}
                    <span className="text-xs opacity-70 text-right tabular-nums">{emUso}</span>
                    <span className="flex items-center justify-end gap-1">
                      {/* O olho ([DA-813]): esconde SO' desta lista. Na de usuario fica cinza com o
                          motivo — ela sai sozinha, o olho nao manda nela ([DA-468]: cinza, nao some). */}
                      {alternandoOlhoId === et.id ? (
                        <Loader2 size={14} className="animate-spin opacity-60" />
                      ) : (
                        <button
                          type="button"
                          onClick={() => alternarEscondida(et)}
                          disabled={deUsuario(et)}
                          title={deUsuario(et)
                            ? 'Etiqueta de usuário: já fica fora desta lista sozinha'
                            : et.escondida_gerenciar
                              ? 'Escondida desta lista — clique para voltar a mostrar'
                              : 'Esconder desta lista (só do Gerenciar; continua valendo nos contatos e nas outras telas)'}
                          className="p-1 rounded hover:bg-black/5 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          {et.escondida_gerenciar || deUsuario(et) ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setDraft({ id: et.id, nome: et.nome, cor: et.cor, apelido: et.apelido })}
                        className="text-xs px-2 py-1 rounded hover:bg-black/5"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => excluir(et)}
                        disabled={excluindoId === et.id || emUso > 0}
                        className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent flex items-center gap-1"
                      >
                        {excluindoId === et.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      </button>
                    </span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-start flex-wrap">
                      <input
                        type="text"
                        value={draft.nome}
                        onChange={(e) => setDraft({ ...draft, nome: e.target.value })}
                        maxLength={60}
                        autoFocus
                        className="flex-1 min-w-[160px] px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                        style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                      />
                      <input
                        type="text"
                        value={draft.apelido ?? ''}
                        onChange={(e) => setDraft({ ...draft, apelido: upperMantendoCursor(e) })}
                        placeholder="Apelido"
                        maxLength={30}
                        className="flex-1 min-w-[160px] px-2.5 py-1.5 rounded border text-sm outline-none focus:ring-2 focus:ring-blue-400"
                        style={{ borderColor: 'var(--border, rgba(0,0,0,0.15))', fontSize: '16px' }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {PALETA.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setDraft({ ...draft, cor: c })}
                          className="w-5 h-5 rounded-full border-2 transition"
                          style={{ backgroundColor: c, borderColor: draft.cor === c ? '#000' : 'transparent' }}
                          title={c}
                        />
                      ))}
                      <span className="ml-2 self-center">
                        <BadgeEtiqueta nome={draft.nome || 'PREVIEW'} cor={draft.cor} apelido={draft.apelido} />
                      </span>
                    </div>
                    <div className="flex justify-end items-center gap-2">
                      {(!draft.nome.trim() || !draft.apelido?.trim()) && (
                        <span className="text-[11px] mr-auto text-amber-700">
                          {!draft.nome.trim() ? 'Falta o nome.' : 'Falta o apelido — e o texto da pillzinha.'}
                        </span>
                      )}
                      <button type="button" className="btn-secundario text-xs" onClick={() => setDraft(null)} disabled={salvandoId === et.id}>Cancelar</button>
                      <button
                        type="button"
                        onClick={salvarEdicao}
                        disabled={!draft.nome.trim() || !draft.apelido?.trim() || salvandoId === et.id}
                        className="btn-primario text-xs flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {salvandoId === et.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Salvar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
